import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { catalogApi } from '../../services/api/catalog.api';
import { CatalogProgram } from '../../types/catalog.response';
import { usePinnedSidebar } from '../../hooks/usePinnedSidebar';

export default function ProgramsList() {
    const [majors, setMajors] = useState<CatalogProgram[]>([]);
    const [minors, setMinors] = useState<CatalogProgram[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [filters, setFilters] = useState({
        majors: false,
        minors: false
    });
    const [selectedColleges, setSelectedColleges] = useState<Set<string>>(new Set());
    const { sidebarContainerRef, sidebarContentRef, containerStyle, contentClassName, contentStyle } = usePinnedSidebar({
        scrollableSidebar: false,
    });

    useEffect(() => {
        const fetchPrograms = async () => {
            try {
                setLoading(true);
                const programsData = await catalogApi.getPrograms();
                const majorsData = programsData.filter((program: CatalogProgram) => program.type === 'major');
                const minorsData = programsData.filter((program: CatalogProgram) => program.type === 'minor');
                setMajors(majorsData);
                setMinors(minorsData);
                setError(null);
            } catch (err: any) {
                setError(err.message || 'Failed to load programs');
            } finally {
                setLoading(false);
            }
        };

        fetchPrograms();
    }, []);

    const allColleges = useMemo(() => {
        const map = new Map<string, string>();
        [...majors, ...minors].forEach(p => {
            p.colleges.forEach(c => {
                if (!map.has(c.collegeId)) map.set(c.collegeId, c.name);
            });
        });
        return Array.from(map.entries())
            .map(([collegeId, name]) => ({ collegeId, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [majors, minors]);

    const filteredPrograms = useMemo(() => {
        let all: CatalogProgram[] = [...majors, ...minors];

        // 1. A-Z Sorting
        all.sort((a, b) => a.name.localeCompare(b.name));

        // 2. Search Filter
        const q = searchQuery.trim().toLowerCase();
        if (q) {
            all = all.filter(p =>
                p.name.toLowerCase().includes(q)
            );
        }

        // 3. Type Filter (Major/Minor)
        const showAll = (filters.majors && filters.minors) || (!filters.majors && !filters.minors);
        if (!showAll) {
            if (filters.majors) {
                all = all.filter(p => p.type === 'major');
            }
            if (filters.minors) {
                all = all.filter(p => p.type === 'minor');
            }
        }

        // 4. College Filter
        if (selectedColleges.size > 0) {
            all = all.filter(p => p.colleges.some(c => selectedColleges.has(c.collegeId)));
        }

        return all;
    }, [majors, minors, searchQuery, filters, selectedColleges]);


    const handleFilterChange = (type: 'majors' | 'minors') => {
        setFilters(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    const handleCollegeToggle = (collegeId: string) => {
        setSelectedColleges(prev => {
            const next = new Set(prev);
            if (next.has(collegeId)) {
                next.delete(collegeId);
            } else {
                next.add(collegeId);
            }
            return next;
        });
    };

    return (
        <div className="px-4 py-6 md:p-8 mt-6 max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-12">
                {/* Left side: Program List */}
                <div className="flex-1">
                    <h1 className="text-lg md:text-3xl font-bold mb-8 text-gray-900">Programs</h1>

                    {loading && (
                        <div className="flex justify-center py-16">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 p-4 rounded mb-6 border border-red-100">
                            <p className="text-red-700 font-medium">{error}</p>
                            <button
                                className="mt-3 text-sm text-red-700 underline"
                                onClick={() => {
                                    setError(null);
                                    setLoading(true);
                                    // Re-run fetch
                                    (async () => {
                                        try {
                                            const programsData = await catalogApi.getPrograms();
                                            const majorsData = programsData.filter((program: CatalogProgram) => program.type === 'major');
                                            const minorsData = programsData.filter((program: CatalogProgram) => program.type === 'minor');
                                            setMajors(majorsData);
                                            setMinors(minorsData);
                                        } catch (err: any) {
                                            setError(err.message || 'Failed to load programs');
                                        } finally {
                                            setLoading(false);
                                        }
                                    })();
                                }}
                            >
                                Try again
                            </button>
                        </div>
                    )}

                    {!loading && !error && filteredPrograms.length === 0 && (
                        <div className="text-center py-16">
                            <p className="text-gray-500 text-lg">No programs found matching your criteria.</p>
                        </div>
                    )}

                    {!loading && !error && (
                        <div className="divide-y divide-gray-200">
                            {filteredPrograms.map((p) => (
                                <Link
                                    key={p.programId}
                                    to={`/programs/${p.programId}`}
                                    className="block py-3 md:py-6 group hover:bg-gray-50 transition-colors px-4 -mx-4 rounded-lg"
                                >
                                    <div className="flex flex-col gap-2">
                                        <div className="text-base md:text-xl font-semibold text-blue-600 group-hover:text-blue-700">
                                        <span className="truncate">{p.name}{p.type === 'minor' ? ' Minor' : ''}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            {p.colleges.map((c) => (
                                                <div key={c.collegeId} className="text-xs md:text-sm text-gray-500">
                                                    {c.name}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}

                    {!loading && !error && (
                        <div className="mt-12 pt-6 border-t border-gray-200">
                            <p className="text-sm text-gray-500">
                                Don't see your program?{' '}
                                <a
                                    href="https://forms.gle/yGzNkoLcPaq8JZih9"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 underline hover:text-blue-700"
                                >
                                    Request it here
                                </a>
                            </p>
                        </div>
                    )}
                </div>

                {/* Right side: Sidebar Filters */}
                <div
                    ref={sidebarContainerRef}
                    className="w-full md:w-64 shrink-0 self-start"
                    style={containerStyle}
                >
                    <div
                        ref={sidebarContentRef}
                        className={contentClassName}
                        style={contentStyle}
                    >
                    <div className="hidden md:block space-y-8">
                        {/* Search Section */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Search</h3>
                            <div className="relative">
                                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
                                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                    </svg>
                                </span>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search programs..."
                                    className="w-full rounded-lg border border-gray-300 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
                                />
                            </div>
                        </div>

                        {/* Filters Section */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Filters</h3>
                            <div className="space-y-3">
                                <label className="flex items-center group cursor-pointer">
                                    <div className="relative flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={filters.majors}
                                            onChange={() => handleFilterChange('majors')}
                                            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 bg-white checked:bg-blue-600 checked:border-blue-600 transition-all focus:outline-none"
                                        />
                                        <svg
                                            className="absolute h-3.5 w-3.5 left-0.5 top-0.5 pointer-events-none hidden peer-checked:block text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                    </div>
                                    <span className="ml-3 text-gray-700 group-hover:text-blue-600 transition-colors">Majors</span>
                                </label>

                                <label className="flex items-center group cursor-pointer">
                                    <div className="relative flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={filters.minors}
                                            onChange={() => handleFilterChange('minors')}
                                            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 bg-white checked:bg-blue-600 checked:border-blue-600 transition-all focus:outline-none"
                                        />
                                        <svg
                                            className="absolute h-3.5 w-3.5 left-0.5 top-0.5 pointer-events-none hidden peer-checked:block text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                    </div>
                                    <span className="ml-3 text-gray-700 group-hover:text-blue-600 transition-colors">Minors</span>
                                </label>
                            </div>
                        </div>
                        
                        {/* Colleges Section */}
                        {allColleges.length > 0 && (
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Colleges</h3>
                                <div className="space-y-3">
                                    {allColleges.map(({ collegeId, name }) => (
                                        <label key={collegeId} className="flex items-center group cursor-pointer">
                                            <div className="relative flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedColleges.has(collegeId)}
                                                    onChange={() => handleCollegeToggle(collegeId)}
                                                    className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 bg-white checked:bg-blue-600 checked:border-blue-600 transition-all focus:outline-none"
                                                />
                                                <svg
                                                    className="absolute h-3.5 w-3.5 left-0.5 top-0.5 pointer-events-none hidden peer-checked:block text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                            </div>
                                            <span className="ml-3 text-gray-700 group-hover:text-blue-600 transition-colors">{name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="pt-4 border-t border-gray-100">
                            <p className="text-xs text-gray-400">
                                Showing {filteredPrograms.length} programs
                            </p>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
